import { CategoryData, WordData } from '../../../../types/types';
import { templeImages, TempleImage } from '../templeImages';

// Nœud « Sur les murs des temples » : une fiche par photographie de
// templeImages.ts, puis quelques questions posées sur les photos.
// Les renvois « voir la fiche « X » dans Y » sont résolus par
// extractCrossReferences (utils/hieroglyphReading.ts), qui ne lit que le
// PREMIER mot de la catégorie (« Objets », « Lire »…).

const firstWord = (category: string) => category.split(' ')[0];

const photoOf = (img: TempleImage): NonNullable<WordData['photo']> => ({
  src: img.src,
  title: img.title,
  titleEn: img.titleEn,
  caption: `${img.site} · ${img.period}`,
  captionEn: `${img.site} · ${img.periodEn}`,
  credit: `${img.credit.author} — ${img.credit.license}`,
  sourceUrl: img.credit.sourceUrl,
});

const signLine = (img: TempleImage, lang: 'fr' | 'en') =>
  img.signs
    .map((s) => {
      const code = s.gardiner ? `${s.gardiner} : ` : '';
      const doubt = s.tentative ? (lang === 'fr' ? ' (lecture proposée)' : ' (proposed reading)') : '';
      const ref = s.link ? ` (voir la fiche « ${s.link.key} » dans ${firstWord(s.link.category)})` : '';
      return `${code}${lang === 'fr' ? s.note : s.noteEn}${doubt}${ref}`;
    })
    .join(' ');

const photoCard = (img: TempleImage): WordData => ({
  translation: `${img.title} – ${img.site}`,
  translationEn: `${img.titleEn} – ${img.site}`,
  reading: `Lecture analytique — ${img.description} Ce qu'on y repère : ${signLine(img, 'fr')}`,
  readingEn: `Analytical reading — ${img.descriptionEn} What to spot: ${signLine(img, 'en')}`,
  photo: photoOf(img),
});

const byId = (id: string) => {
  const img = templeImages.find((i) => i.id === id);
  if (!img) throw new Error(`templeImages: image « ${id} » introuvable`);
  return img;
};

const quizCard = (
  id: string,
  quiz: NonNullable<WordData['quiz']>,
): WordData => {
  const img = byId(id);
  return {
    translation: `${img.title} – ${img.site}`,
    translationEn: `${img.titleEn} – ${img.site}`,
    photo: photoOf(img),
    quiz,
  };
};

const entries: CategoryData = {};
templeImages.forEach((img) => {
  entries[img.title] = photoCard(img);
});

entries['Question : la tuile de Ramsès III'] = quizCard('medinet-habu-tuile', {
  question: 'Sur cette tuile, que désigne le vanneau aux mains levées ?',
  options: ['Le roi', 'Le peuple', 'Un dieu oiseau', 'Le ciel'],
  correctIndex: 1,
  explanation:
    'Le vanneau huppé (G23) aux mains levées désigne rḫyt, « le peuple » : avec l\'étoile (dwꜣ, « adorer ») et la corbeille (nb, « tout »), la tuile dit « que tout le peuple adore » le roi.',
  questionEn: 'On this tile, what does the lapwing with raised hands stand for?',
  optionsEn: ['The king', 'The people', 'A bird god', 'The sky'],
  explanationEn:
    'The crested lapwing (G23) with raised hands stands for rḫyt, "the people": together with the star (dwꜣ, "to adore") and the basket (nb, "all"), the tile says "may all the people adore" the king.',
});

entries['Question : l\'abeille d\'Abydos'] = quizCard('abydos-relief', {
  question: 'À gauche du linteau, l\'abeille fait partie du titre nsw-bỉty. Que signifie ce titre ?',
  options: [
    'Roi de Haute et de Basse-Égypte',
    'Maître du miel',
    'Fils de Rê',
    'Grand prêtre d\'Osiris',
  ],
  correctIndex: 0,
  explanation:
    'Le jonc (M23, nsw) représente la Haute-Égypte et l\'abeille (L2, bỉty) la Basse-Égypte : nsw-bỉty, « roi de Haute et de Basse-Égypte ».',
  questionEn: 'On the left of the lintel, the bee is part of the title nsw-bỉty. What does this title mean?',
  optionsEn: [
    'King of Upper and Lower Egypt',
    'Master of honey',
    'Son of Ra',
    'High priest of Osiris',
  ],
  explanationEn:
    'The sedge (M23, nsw) stands for Upper Egypt and the bee (L2, bỉty) for Lower Egypt: nsw-bỉty, "King of Upper and Lower Egypt".',
});

entries['Question : les deux couronnes d\'Edfou'] = quizCard('edfou-temple', {
  question: 'La déesse de gauche porte la couronne blanche. Quelle région représente-t-elle ?',
  options: ['La Basse-Égypte (le Delta)', 'La Haute-Égypte (le Sud)', 'La Nubie', 'Le désert'],
  correctIndex: 1,
  explanation:
    'La couronne blanche (ḥḏ.t) est celle de la Haute-Égypte, portée ici par Nekhbet ; la couronne rouge (dšr.t) d\'Ouadjet est celle de la Basse-Égypte. Le roi porte les deux à la fois : le pschent.',
  questionEn: 'The goddess on the left wears the white crown. Which region does she stand for?',
  optionsEn: ['Lower Egypt (the Delta)', 'Upper Egypt (the South)', 'Nubia', 'The desert'],
  explanationEn:
    'The white crown (ḥḏ.t) is that of Upper Egypt, worn here by Nekhbet; Wadjet\'s red crown (dšr.t) is that of Lower Egypt. The king wears both at once: the pschent.',
});

entries['Question : la pierre de Rosette'] = quizCard('pierre-de-rosette', {
  question: 'Combien d\'écritures différentes porte la pierre de Rosette ?',
  options: ['Une', 'Deux', 'Trois', 'Quatre'],
  correctIndex: 2,
  explanation:
    'Trois : les hiéroglyphes en haut, le démotique au milieu et le grec en bas. Le grec, lisible, a servi de clé à Champollion.',
  questionEn: 'How many different scripts are on the Rosetta Stone?',
  optionsEn: ['One', 'Two', 'Three', 'Four'],
  explanationEn:
    'Three: hieroglyphs at the top, Demotic in the middle and Greek at the bottom. The readable Greek gave Champollion the key.',
});

export const monuments: CategoryData = entries;

export default monuments;
